import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import { HubItemKind } from "./hubItemCategories";
import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import { KnowledgeItemPreviewItemModel } from "./KnowledgeItems/knowledgeItemViewer";
import { EventPreviewModel } from "./Events/eventViewer";
import { UserIdentityModel } from "../UserProfile/service";
import { HubItemCategoryNodeKind, ReactionCounterModel } from "./common";
import { CommunityKind } from "../Communities/community";
import { CommunityPostImageViewModel } from "../Communities/communityPostViewer";
import { CommunityViewModel } from "../Communities/communityPosts";

export enum HubDataSource {
	Announcement = 1,
	KnowledgeItem = 2,
	Event = 3,
	Community = 4
}

export type HubItemDataSource =
	| HubDataSource.Announcement
	| HubDataSource.KnowledgeItem
	| HubDataSource.Event;

export enum HubItemCategoryKind {
	Uncategorized = 1,
	Custom = 2
}

export type HubItemCategoryModel = {
	id: number;
	name: string;
	kind: HubItemCategoryKind;
	canCreateItem: boolean;
	isFollowing: boolean;
	hubItemKind: HubItemKind;
};

export type HubItemCategoryListItemModel = {
	id: number;
	name: string;
};

export type HubItemCategorySidebarNodeModel = {
	kind: HubItemCategoryNodeKind.Category;
	category: HubItemCategoryModel;
	newItemsCount: number;
};

export type HubItemGroupHubSidebarNodeModel = {
	kind: HubItemCategoryNodeKind.Group;
	groupId: number;
	name: string;
	children: readonly HubItemHubSidebarNodeModel[];
};

export type HubItemHubSidebarNodeModel =
	| HubItemGroupHubSidebarNodeModel
	| HubItemCategorySidebarNodeModel;

export type HubItemRootHubSidebarNodeModel = {
	source:
		| HubDataSource.Announcement
		| HubDataSource.KnowledgeItem
		| HubDataSource.Event;
	newItemsCount: number;
	children: readonly HubItemHubSidebarNodeModel[];
};

export type CommunityHubSidebarNodeModel = {
	id: number;
	kind: CommunityKind;
	name: string;
};

export type CommunityRootHubSidebarNodeModel = {
	source: HubDataSource.Community;
	newCommunityIds: readonly number[];
	children: readonly CommunityHubSidebarNodeModel[];
};

export type RootHubSidebarNodeModel =
	| HubItemRootHubSidebarNodeModel
	| CommunityRootHubSidebarNodeModel;

export type HubPageViewModel = {
	categoryHierarchy: readonly RootHubSidebarNodeModel[];
	hubItemKindsCanCreateNewCategoriesFor: readonly HubItemKind[];
	canAccessHubManagementForCommunities: boolean;
	canManageCommunityMembershipRequests: boolean;
	canManageSidebar: boolean;
	canCreateCommunity: boolean;
	canCreateCommunityPost: boolean;
};

export function getPageViewModel(signal?: AbortSignal): Promise<HubPageViewModel> {
	return httpClient.get<HubPageViewModel>(
		"UiApi/Hub",
		undefined,
		{ signal }
	);
}

export function listCategories(
	hubItemKind: HubItemKind | undefined,
	signal?: AbortSignal
): Promise<readonly HubItemCategoryModel[]> {
	return httpClient.get<readonly HubItemCategoryModel[]>(
		"UiApi/Hub/Categories",
		{ hubItemKind },
		{ signal }
	);
}

export function getHierarchy(
	signal?: AbortSignal
): Promise<readonly RootHubSidebarNodeModel[]> {
	return httpClient.get<readonly RootHubSidebarNodeModel[]>(
		"UiApi/Hub/Hierarchy",
		undefined,
		{ signal }
	);
}

export function unregisterFollowCategory(categoryId: number, signal?: AbortSignal): Promise<void> {
	return httpClient.delete<void>(
		`/UiApi/Hub/Categories/${categoryId}/Subscription`,
		undefined,
		{ signal }
	);
}

export function registerFollowCategory(categoryId: number, signal?: AbortSignal): Promise<void> {
	return httpClient.post<void>(
		`/UiApi/Hub/Categories/${categoryId}/Subscription`,
		undefined,
		{ signal }
	);
}

export type HubOverviewModel = {
	announcements: readonly AnnouncementPreviewItemModel[];
	events: readonly EventPreviewModel[];
	knowledgeItems: readonly KnowledgeItemPreviewItemModel[];
	communityPosts: OptionalModel<readonly CommunityPostOverviewItemModel[]>;
};

export type CommunityPostOverviewItemModel = {
	id: number;
	message: string | null;
	images: readonly CommunityPostImageViewModel[];
	pollQuestion: string | null;
	createdOn: string | Date;
	createdBy: UserIdentityModel | null;
	community: CommunityViewModel;
	statistics: CommunityPostOverviewStatistics;
	hasLinkedDocuments: boolean;
};

export type CommunityPostOverviewStatistics = {
	reactionCounters: readonly ReactionCounterModel[];
	commentCount: number;
};

export function listItems(signal?: AbortSignal): Promise<HubOverviewModel> {
	return httpClient.get<HubOverviewModel>(
		"UiApi/Hub/Items",
		undefined,
		{ signal }
	);
}

export function findUsers(
	searchTerm: string,
	amount: number,
	signal?: AbortSignal
): Promise<readonly UserIdentityModel[]> {
	return httpClient.get<readonly UserIdentityModel[]>(
		`UiApi/Hub/Users`,
		{
			searchTerm,
			amount
		},
		{ signal }
	);
}

export function createDummyData(
	hubItemKinds: readonly HubItemKind[],
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"/UiApi/Hub/CreateDummyData",
		{ hubItemKinds },
		{ signal }
	);
}

export function listKindsWithoutContent(signal?: AbortSignal): Promise<readonly HubItemKind[]> {
	return httpClient.get<readonly HubItemKind[]>(
		"/UiApi/Hub/KindsWithoutContent",
		undefined,
		{ signal }
	);
}